import React from 'react';
import T from 'i18n';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Checkbox,
  TableSortLabel,
  IconButton,
} from '@mui/material';
import Box from '@mui/material/Box';
import Tooltip from '@mui/material/Tooltip';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { useDrag } from 'react-dnd';
import { getEmptyImage } from 'react-dnd-html5-backend';
import history from 'common/history';
import { useDispatch, useSelector } from 'react-redux';
import { setCurrentSelectedFile } from '../redux/setCurrentSelectedFile';
import { fetchFileDetails } from '../redux/fetchFileDetails';
import { selectCurrentCaseId, selectDocuments } from 'common/selectors';

export type RelevantFileRow = {
  id: string;
  citationsCount: number;
  name: string;
  author?: string;
  recipient?: string;
  docDate?: string | null;
};

type Order = 'asc' | 'desc';

function descendingComparator<T extends Record<string, any>>(a: T, b: T, orderBy: keyof T) {
  const aValue = a[orderBy] as any;
  const bValue = b[orderBy] as any;
  if (bValue === undefined || bValue === null) return -1;
  if (aValue === undefined || aValue === null) return 1;
  if (bValue < aValue) return -1;
  if (bValue > aValue) return 1;
  return 0;
}

function getComparator<T extends Record<string, any>>(
  order: Order,
  orderBy: keyof T,
): (a: T, b: T) => number {
  return order === 'desc'
    ? (a, b) => descendingComparator<T>(a, b, orderBy)
    : (a, b) => -descendingComparator<T>(a, b, orderBy);
}

function stableSort<T>(array: readonly T[], comparator: (a: T, b: T) => number) {
  const stabilizedThis = array.map((el, index) => [el, index] as [T, number]);
  stabilizedThis.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) return order;
    return a[1] - b[1];
  });
  return stabilizedThis.map((el) => el[0]);
}

export interface RelevantFilesTableProps {
  rows: RelevantFileRow[];
  height?: number | string;
  maxHeight?: number | string;
  multiSelect?: boolean;
  selectedIds?: string[];
  onSelectionChange?: (ids: string[]) => void;
  columnWidths?: Partial<Record<keyof RelevantFileRow, number>>;
}

const ClampedCellText: React.FC<{ text?: string; maxLines?: number; placement?: 'bottom' | 'left' | 'right' | 'top' }> = ({
  text,
  maxLines = 2,
  placement = 'top',
}) => {
  const spanRef = React.useRef<HTMLSpanElement | null>(null);
  const [isTruncated, setIsTruncated] = React.useState(false);

  React.useEffect(() => {
    const el = spanRef.current;
    if (!el) return;
    const check = () => {
      const truncated = el.scrollHeight > el.clientHeight || el.scrollWidth > el.clientWidth;
      setIsTruncated(truncated);
    };
    check();
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, [text, maxLines]);

  const content = (
    <Box
      component="span"
      ref={spanRef}
      sx={{
        display: '-webkit-box',
        WebkitLineClamp: maxLines,
        WebkitBoxOrient: 'vertical',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'normal',
        wordBreak: 'break-word',
        maxWidth: '100%',
      }}
    >
      {text || ''}
    </Box>
  );

  return isTruncated ? (
    <Tooltip title={text || ''} placement={placement} arrow>
      {content}
    </Tooltip>
  ) : (
    content
  );
};

export default function RelevantFilesTable({
  rows,
  height,
  maxHeight,
  multiSelect = true,
  selectedIds,
  onSelectionChange,
  columnWidths,
}: RelevantFilesTableProps) {
  const dispatch = useDispatch();
  const caseId = useSelector(selectCurrentCaseId);
  const documents = useSelector(selectDocuments) as Array<any>;
  const selectedIdsRef = React.useRef<string[]>([]);
  const fileDetailsCache = React.useRef<{ [key: string]: any }>({});
  const [order, setOrder] = React.useState<Order>('asc');
  const [orderBy, setOrderBy] = React.useState<keyof RelevantFileRow>('citationsCount');
  const [internalSelected, setInternalSelected] = React.useState<string[]>([]);

  const effectiveSelected = selectedIds ?? internalSelected;
  const isSelected = (id: string) => effectiveSelected.indexOf(id) !== -1;

  React.useEffect(() => {
    selectedIdsRef.current = effectiveSelected;
  }, [effectiveSelected]);

  const handleRequestSort = (property: keyof RelevantFileRow) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const loadFileWithCache = React.useCallback(
    async (
      fileId: string,
      onSuccess: (fileDetails: any) => void,
      cacheRef: React.MutableRefObject<{ [key: string]: any }> = fileDetailsCache,
    ) => {
      const cacheKey = `${caseId}-${fileId}`;
      const cachedFileDetails = cacheRef.current[cacheKey];

      if (cachedFileDetails) {
        // Use cached data instead of making API call
        onSuccess(cachedFileDetails);
        return cachedFileDetails;
      } else {
        // Check if fileId exists in the documents array
        const fileFromDocuments =
          documents && documents.length > 0 ? documents.find((doc) => doc.id === fileId) : null;

        if (fileFromDocuments) {
          // Use the file object from documents array
          cacheRef.current[cacheKey] = fileFromDocuments;
          onSuccess(fileFromDocuments);
          return fileFromDocuments;
        } else {
          // Make API call and cache the result
          try {
            const res = await dispatch(
              fetchFileDetails({ caseId, fileId, firstTrialBookOnly: true }),
            );
            // Spread the first bundleLocations object into the main res object
            const updateFileObjectWithLocation =
              res.bundleLocations && res.bundleLocations.length > 0
                ? { ...res, ...res.bundleLocations[0] }
                : res;
            // Store the updated result in cache for future use
            cacheRef.current[cacheKey] = updateFileObjectWithLocation;
            onSuccess(updateFileObjectWithLocation);
            return updateFileObjectWithLocation;
          } catch (error) {
            console.error('Error fetching file details:', error);
          }
        }
      }
    },
    [caseId, documents, dispatch],
  );

  const handleClick = (id: string) => {
    let newSelected: string[] = [];
    if (multiSelect) {
      if (isSelected(id)) {
        newSelected = effectiveSelected.filter((s) => s !== id);
      } else {
        newSelected = [...effectiveSelected, id];
      }
    } else {
      newSelected = isSelected(id) ? [] : [id];
    }
    if (selectedIds === undefined) setInternalSelected(newSelected);
    onSelectionChange && onSelectionChange(newSelected);
  };

  const handleRowDoubleClick = (fileId: string) => {
    history.push(
      history.location.pathname.replace(/\/files.*/, ``) + `/files/${fileId}?askAI=true`,
    );
    loadFileWithCache(fileId, (fileDetails) => {
      dispatch(setCurrentSelectedFile(fileDetails));
    });
  };

  const handleSelectAll = (checked: boolean) => {
    const allIds = checked ? rows.map((r) => r.id) : [];
    if (selectedIds === undefined) setInternalSelected(allIds);
    onSelectionChange && onSelectionChange(allIds);
  };

  const [copiedId, setCopiedId] = React.useState<string | null>(null);

  const handleCopyId = (event: React.MouseEvent, id: string) => {
    event.stopPropagation();
    navigator.clipboard.writeText(id).then(
      () => {
        setCopiedId(id);
        setTimeout(() => setCopiedId(null), 10000); // Reset after 10 seconds
      },
      (err) => {
        console.error('Failed to copy ID:', err);
      }
    );
  };

  const numSelected = effectiveSelected.length;
  const rowCount = rows.length;

  const sortedRows = React.useMemo(
    () => stableSort<RelevantFileRow>(rows, getComparator<RelevantFileRow>(order, orderBy)),
    [rows, order, orderBy],
  );

  const widths = React.useMemo(() => {
    const defaults: Record<keyof RelevantFileRow, number> = {
      id: 100,
      name: 200,
      citationsCount: 60,
      author: 80,
      recipient: 80,
      docDate: 120,
    };
    return { ...defaults, ...(columnWidths || {}) } as Record<keyof RelevantFileRow, number>;
  }, [columnWidths]);

  const DraggableRelevantFileRow: React.FC<{
    row: RelevantFileRow;
    allRows: RelevantFileRow[];
    selected: boolean;
    index: number;
    onDoubleClick: () => void;
    children?: React.ReactNode;
  }> = ({ row, allRows, selected, index, onDoubleClick, children }) => {
    const [, drag, preview] = useDrag(() => ({
      type: 'DOCUMENT',
      canDrag: () => {
        const selectedIdsLocal = selectedIdsRef.current || [];
        return selectedIdsLocal.length > 0 && selectedIdsLocal.includes(row.id);
      },
      item: () => {
        const selectedIdsLocal = selectedIdsRef.current || [];
        const useIds = selectedIdsLocal;
        const docs = allRows
          .filter((r) => useIds.includes(r.id))
          .map((r) => ({ id: r.id, name: r.name }));
        return {
          documentIds: useIds,
          dragRow: { id: row.id, name: row.name },
          type: 'DOCUMENT',
          isAttachment: true,
          attachmentDocuments: docs,
        };
      },
      options: { dropEffect: 'copy' },
    }));

    React.useEffect(() => {
      preview(getEmptyImage(), { captureDraggingState: true });
    }, [preview]);

    return (
      <TableRow
        ref={drag}
        hover
        onDoubleClick={onDoubleClick}
        role="checkbox"
        aria-checked={selected}
        tabIndex={-1}
        key={row.id}
        selected={selected}
        sx={{
          cursor: 'pointer',
          backgroundColor: index % 2 === 0 ? '#FFFFFF' : '#E5E5E5',
        }}
      >
        {children}
      </TableRow>
    );
  };

  return (
    <TableContainer
      component={Paper}
      sx={{
        maxHeight,
        height,
        width: '98%',
        overflowX: 'auto',
        marginTop: '1rem',
        overflowY: height || maxHeight ? 'auto' : 'visible',
        boxShadow: 'none',
      }}
    >
      <Table stickyHeader size="small" aria-label="relevant-files-table">
        <TableHead>
          <TableRow sx={{ '& th': { backgroundColor: '#fff', zIndex: 0 } }}>
            <TableCell padding="checkbox">
              {multiSelect ? (
                <Checkbox
                  color="primary"
                  indeterminate={numSelected > 0 && numSelected < rowCount}
                  checked={rowCount > 0 && numSelected === rowCount}
                  onChange={(e) => handleSelectAll(e.target.checked)}
                />
              ) : null}
            </TableCell>
            <TableCell sortDirection={orderBy === 'id' ? order : false} sx={{ whiteSpace: 'nowrap', width: widths.id, minWidth: widths.id, maxWidth: widths.id }}>
              <TableSortLabel
                active={orderBy === 'id'}
                direction={orderBy === 'id' ? order : 'asc'}
                onClick={() => handleRequestSort('id')}
              >
                {T.translate('generic.id')}
              </TableSortLabel>
            </TableCell>
            <TableCell sortDirection={orderBy === 'name' ? order : false} sx={{ width: widths.name, minWidth: widths.name, maxWidth: widths.name }}>
              <TableSortLabel
                active={orderBy === 'name'}
                direction={orderBy === 'name' ? order : 'asc'}
                onClick={() => handleRequestSort('name')}
              >
                {T.translate('generic.name')}
              </TableSortLabel>
            </TableCell>
            <TableCell sortDirection={orderBy === 'citationsCount' ? order : false} sx={{ whiteSpace: 'nowrap', width: widths.citationsCount, minWidth: widths.citationsCount, maxWidth: widths.citationsCount }}>
              <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                <TableSortLabel
                  active={orderBy === 'citationsCount'}
                  direction={orderBy === 'citationsCount' ? order : 'asc'}
                  onClick={() => handleRequestSort('citationsCount')}
                >
                  {T.translate('generic.citationsCount')}
                </TableSortLabel>
              </Box>
            </TableCell>
            <TableCell sortDirection={orderBy === 'docDate' ? order : false} sx={{ whiteSpace: 'nowrap', width: widths.docDate, minWidth: widths.docDate, maxWidth: widths.docDate }}>
              <TableSortLabel
                active={orderBy === 'docDate'}
                direction={orderBy === 'docDate' ? order : 'asc'}
                onClick={() => handleRequestSort('docDate')}
              >
                {T.translate('generic.date')}
              </TableSortLabel>
            </TableCell>
            <TableCell sortDirection={orderBy === 'author' ? order : false} sx={{ width: widths.author, minWidth: widths.author, maxWidth: widths.author }}>
              <TableSortLabel
                active={orderBy === 'author'}
                direction={orderBy === 'author' ? order : 'asc'}
                onClick={() => handleRequestSort('author')}
              >
                {T.translate('generic.author')}
              </TableSortLabel>
            </TableCell>
            <TableCell sortDirection={orderBy === 'recipient' ? order : false} sx={{ width: widths.recipient, minWidth: widths.recipient, maxWidth: widths.recipient }}>
              <TableSortLabel
                active={orderBy === 'recipient'}
                direction={orderBy === 'recipient' ? order : 'asc'}
                onClick={() => handleRequestSort('recipient')}
              >
                {T.translate('generic.recipient')}
              </TableSortLabel>
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {sortedRows.map((row, index) => {
            const labelId = `relevant-files-table-checkbox-${row.id}`;
            const selected = isSelected(row.id);
            return (
              <DraggableRelevantFileRow
                key={row.id}
                row={row}
                allRows={sortedRows}
                selected={selected}
                index={index}
                onDoubleClick={() => handleRowDoubleClick(row.id)}
              >
                <TableCell 
                  padding="checkbox"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleClick(row.id);
                  }}
                >
                  {multiSelect ? (
                    <Checkbox color="primary" checked={selected} inputProps={{ 'aria-labelledby': labelId }} />
                  ) : (
                    <Checkbox color="primary" checked={selected} inputProps={{ 'aria-labelledby': labelId }} />
                  )}
                </TableCell>
                <TableCell 
                  component="th" 
                  id={labelId} 
                  scope="row" 
                  sx={{ 
                    whiteSpace: 'nowrap', 
                    width: widths.id, 
                    minWidth: widths.id, 
                    maxWidth: widths.id,
                    cursor: 'pointer',
                    '&:hover': {
                      backgroundColor: 'rgba(0, 0, 0, 0.04)',
                    },
                    '&:hover .copy-icon': {
                      opacity: 1,
                    }
                  }}
                  onClick={(e) => handleCopyId(e, row.id)}
                >
                  <Tooltip 
                    title={copiedId === row.id ? T.translate('generic.copied') : T.translate('generic.clickToCopyId')} 
                    placement="top" 
                    arrow
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, justifyContent: 'space-between' }}>
                      <span>{row.id}</span>
                      <IconButton
                        size="small"
                        onClick={(e) => handleCopyId(e, row.id)}
                        className="copy-icon"
                        sx={{ 
                          padding: '2px',
                          opacity: 0,
                          transition: 'opacity 0.2s',
                        }}
                      >
                        <ContentCopyIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  </Tooltip>
                </TableCell>
                <TableCell sx={{ width: widths.name, minWidth: widths.name, maxWidth: widths.name }}>
                  <ClampedCellText text={row.name} maxLines={2} />
                </TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap', width: widths.citationsCount, minWidth: widths.citationsCount, maxWidth: widths.citationsCount }}>{row.citationsCount ?? 0}</TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap', width: widths.docDate, minWidth: widths.docDate, maxWidth: widths.docDate }}>{row.docDate || ''}</TableCell>
                <TableCell sx={{ width: widths.author, minWidth: widths.author, maxWidth: widths.author }}>
                  <ClampedCellText text={row.author || ''} maxLines={2} />
                </TableCell>
                <TableCell sx={{ width: widths.recipient, minWidth: widths.recipient, maxWidth: widths.recipient }}>
                  <ClampedCellText text={row.recipient || ''} maxLines={2} />
                </TableCell>
              </DraggableRelevantFileRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
}


